import React, { useEffect, useRef, useState } from 'react';
import { FieldInputProps, useField } from 'formik';
import { OnChangeValue } from 'react-select';
import styled from 'styled-components';
import { useLocalStorage } from 'usehooks-ts';

import { DepartureOption } from '@lt/api/dist/types/departure';

import GeolocationTooltip from '@lt/components/components/GeolocationTooltip';
import { addStep, useCigarette, Values } from '../../context';
import { FIELD_NAMES, storageKeys } from '../../constants';
import { DeparturePicker, DeparturePickerProps } from '../DeparturePicker/DeparturePicker';
import { afterDepartureStep, isDepartureStep } from '../../utils';
import { useFetchAvailableDepartures } from '../../hooks/useFetchAvailableDepartures';
import { InputActionMeta } from 'react-select/dist/declarations/src/types';

type FormDeparturePickerProps = FieldInputProps<DeparturePickerProps['options']> & {
  className?: DeparturePickerProps['className'];
  isLoading?: boolean;
  isMainPage?: boolean;
  popupCityName: string | null;
  setPopupCityName: (newValue: string | null) => void;
  variant?: 'default' | 'hotel';
};

export const FormDeparturePicker = ({
  value: options,
  name,
  isLoading,
  className,
  popupCityName,
  setPopupCityName,
  isMainPage = false,
  variant = 'default',
}: FormDeparturePickerProps) => {
  const [open, setOpen] = useState(false);
  const [isChooseAnyDeparture, setIsChooseAnyDeparture] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const [{ value }, { error }, { setValue, setError }] = useField<Values['departure']>(name);
  const {
    state: { steps, isMobile, isWizard },
    dispatch,
  } = useCigarette();
  const [savedDeparture, setSavedDeparture] = useLocalStorage<DepartureOption | null>(
    storageKeys.departure,
    null
  );
  const [inputValue, setInputValue] = useState<string | undefined>(undefined);

  const { optionsWithAvailability } = useFetchAvailableDepartures(options, isMobile);

  // Используем useEffect из-за того что isMobile приходит позднее рендера компонента
  useEffect(() => {
    if (!isMobile) {
      setInputValue(value?.selectedLabel);
    } else {
      setInputValue(undefined);
    }
  }, [isMobile]);

  useEffect(() => {
    setInputValue(value?.selectedLabel || '');
  }, [value]);

  const menuIsOpenInWizard = isDepartureStep(steps);
  const menuIsOpen = isWizard ? menuIsOpenInWizard : open;
  const isDisabled = isWizard && !menuIsOpen;

  useEffect(() => {
    if (error) {
      openMenu();
    }
  }, [error]);

  const onChange = (
    newValue: OnChangeValue<DepartureOption, false>,
    { action }: { action: string | 'clear' }
  ) => {
    if (action === 'clear' && isMobile) return;

    setValue(newValue || undefined);

    if (newValue) {
      setSavedDeparture(newValue);
    }
    if (isChooseAnyDeparture) {
      setIsChooseAnyDeparture(false);
    }
    if (isWizard) {
      dispatch(addStep(FIELD_NAMES.datepicker));
    }
    closeMenu();
  };

  if (isWizard && !afterDepartureStep(steps)) {
    return null;
  }

  const openMenu = () => {
    setOpen(true);
  };

  const closeMenu = () => {
    setOpen(false);
    setError(undefined);
  };

  const accept = () => {
    setValue(options[0]);
    setSavedDeparture(options[0]);
    setPopupCityName(null);
  };

  const decline = () => {
    openMenu();
    setIsChooseAnyDeparture(true);
    setPopupCityName(null);
  };

  const onInputChange = (currentValue: string, { action }: InputActionMeta) => {
    if (action === 'input-blur' && !isMobile) {
      setInputValue(value ? value.selectedLabel : undefined);
    }

    if (action === 'input-change') {
      setInputValue(currentValue);
    }
  };

  return (
    <FormDepartureContainer
      ref={ref}
      className={className}
      $isMobile={isMobile}
      $isWizard={isWizard}
    >
      <DeparturePicker
        isMobile={isMobile}
        options={optionsWithAvailability || []}
        onChange={onChange}
        menuIsOpen={menuIsOpen}
        isWizard={isWizard}
        isDisabled={isDisabled}
        value={value}
        onMenuOpen={openMenu}
        onMenuClose={closeMenu}
        isLoading={isLoading}
        inputValue={inputValue}
        onInputChange={onInputChange}
        variant={variant}
      />
      {isMainPage && (
        <GeolocationTooltip
          isVisible={!savedDeparture && !!popupCityName}
          targetRef={ref}
          cityName={popupCityName || ''}
          acceptLocation={accept}
          declineLocation={decline}
        />
      )}
    </FormDepartureContainer>
  );
};

const FormDepartureContainer = styled.div<{
  $isMobile?: boolean;
  $isWizard?: boolean;
}>`
  width: ${({ $isMobile }) => ($isMobile ? `100%` : `29.5%`)};
  position: relative;
  ${({ $isMobile, $isWizard }) =>
    $isMobile &&
    !$isWizard &&
    `display: flex;
    flex-direction: column;
    `}
`;
